import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../../components/Form/FormForm';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { t } from '../../../../locales/translate';
import { FormSwitcher } from '../../../../components/Form/form_switcher/form_switcher';
import React from 'react';
import { graphql } from '../../../../generated';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { Stack } from '../../../../components/Stack/Stack';
import { Button } from '../../../../components/Button/Button';
import { QueryNames } from '../../../../model/graphql';
import { FormCheckbox } from '../../../../components/Form/Checkbox/FormCheckbox';
import { ProductEventTable } from '../../../../components/product_event_table/product_event_table';
import { ProductEventTableActions } from '../../../../components/product_event_table/product_event_table_actions';
import { useProductEventTableSelection } from '../../../../model/product_event_table_selection';
import { FilterTag } from '../../../../model/filter';

const MERCH_PRODUCT_CATEGORIES = graphql(`
  query GetMerchProductCategories($first: Int, $shopId: String) {
    categories(first: $first, shopId: $shopId, withProductTypes: [MERCH_PRODUCT]) {
      nodes {
        id
        name
      }
    }
  }
`);

const SHOP_EVENTS_AND_MERCH_PRODUCTS = graphql(`
  query GetShopEventsAndMerchProducts(
    $shopId: ID
    $type: ProductType
    $terms: [String!]
    $first: Int
    $categoryIds: [ID!]
    $voucherCampaignId: ID!
    $after: String
  ) {
    products(shopId: $shopId, type: $type, terms: $terms, first: $first, categoryIds: $categoryIds, after: $after) {
      edges {
        node {
          id
          sku
          artist
          name
          ... on Event {
            venue {
              id
              name
            }
            happeningFrom
          }
          ... on MerchProduct {
            categories {
              nodes {
                id
                name
              }
            }
            productType
            color
            price(countryCode: "de")
            estimatedAvailability
          }
          productVariants {
            nodes {
              id
              sku
              description
              netPrice
              price
              estimatedAvailability
              pricingCategory {
                id
                name
              }
              associatedWithVoucherCampaign(voucherCampaignId: $voucherCampaignId)
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      permissions {
        id
        create
      }
    }
  }
`);

const ADD_PRODUCT_VARIANTS = graphql(`
  mutation AddProductVariants($voucherCampaignId: ID!, $productVariantIds: [ID!]!) {
    addProductVariantsToVoucherCampaign(
      input: { voucherCampaignId: $voucherCampaignId, productVariantIds: $productVariantIds }
    ) {
      voucherCampaign {
        id
      }
    }
  }
`);

const ADD_ALL_PRODUCT_VARIANTS = graphql(`
  mutation AddAllProductVariants(
    $voucherCampaignId: ID!
    $terms: [String!]
    $categoryIds: [ID!]
    $productType: ProductType
  ) {
    addFilteredSelectionToVoucherCampaign(
      input: {
        voucherCampaignId: $voucherCampaignId
        terms: $terms
        categoryIds: $categoryIds
        productType: $productType
      }
    ) {
      voucherCampaign {
        id
      }
    }
  }
`);

interface Props {
  voucherCampaignId: string;
  shopId: string;
  productTab?: boolean;
}

export function ProductsAndEventsCreate({ voucherCampaignId, shopId, productTab = true }: Props): JSX.Element {
  const translation = t.page.voucherCampaign.discountedProductsAndEvents;
  const { open, Modal, size: modalSize, close } = useModal();
  const { model, fields, onSubmit, submissionStatus } = useForm<{
    productTab?: boolean;
    keepModalOpen?: boolean;
    terms: FilterTag[];
    categoryIds: Array<string>;
  }>({
    model: {
      productTab,
      keepModalOpen: false,
      terms: [],
      categoryIds: [],
    },
    onSubmit: async ({ model }) => {
      if (allSelected) {
        await addAllProductVariants({
          variables: {
            voucherCampaignId,
            terms: model.terms.map((item) => `${item.exclude ? '-' : ''}${item.label}`),
            categoryIds: model.categoryIds,
            productType: model.productTab ? 'MERCH_PRODUCT' : 'EVENT',
          },
        });
      }

      if (size === 0) return;
      await addProductVariants({
        variables: {
          voucherCampaignId,
          productVariantIds: items,
        },
      });

      if (!model.keepModalOpen) {
        close();
      } else {
        clear();
      }
    },
  });

  const { data, fetchMore } = useSuspenseQuery(SHOP_EVENTS_AND_MERCH_PRODUCTS, {
    variables: {
      shopId,
      voucherCampaignId,
      type: model.productTab ? 'MERCH_PRODUCT' : 'EVENT',
      first: 10,
      terms: model.terms.map((item) => `${item.exclude ? '-' : ''}${item.label}`),
      categoryIds: model.categoryIds,
    },
  });
  const { data: categoriesData } = useSuspenseQuery(MERCH_PRODUCT_CATEGORIES, {
    variables: {
      shopId,
      first: 1000,
    },
  });

  const categories = categoriesData.categories.nodes;
  const { pageInfo, permissions } = data.products;
  const refetchQueries = [
    QueryNames.VOUCHER_CAMPAIGN_PRODUCT_EVENTS,
    QueryNames.MERCH_PRODUCT_CATEGORIES,
    ...(model.keepModalOpen ? [QueryNames.SHOP_EVENTS_AND_MERCH_PRODUCTS] : []),
  ];

  // Mutations
  const [addProductVariants] = useMutation(ADD_PRODUCT_VARIANTS, {
    refetchQueries,
  });
  const [addAllProductVariants] = useMutation(ADD_ALL_PRODUCT_VARIANTS, {
    refetchQueries,
  });

  const {
    items,
    size,
    handleRowSelect,
    handleSelectAllDisplayed,
    handleSelectAllItems,
    clear,
    selectionType,
    allSelected,
    mappedItems,
  } = useProductEventTableSelection(data.products);

  const titleTranslations = {
    single: translation.singleResultSelected,
    multiple: translation.manyResultsSelected(size),
    all: translation.allSelected,
  };

  return (
    <Modal
      open={open}
      size={modalSize}
      title={size === 0 ? translation.create.productsAndEventsAssociation : titleTranslations[selectionType]}
      actions={
        size > 0 &&
        !allSelected && (
          <Button title={translation.selectAll} type="secondary-transparent" condensed onClick={handleSelectAllItems} />
        )
      }
      footer={
        <Stack line>
          <FormCheckbox {...fields.keepModalOpen} label={translation.create.keepModalOpen} />
          <Button title={translation.create.cancel} onClick={close} />
          <Button
            title={translation.create.add}
            onClick={onSubmit}
            type="primary"
            loading={submissionStatus === 'submitting'}
            disabled={!permissions.create || items.length === 0}
          />
        </Stack>
      }
      withSelectedItems={size > 0}
    >
      <FormForm hideCancel hideSubmit preventDefault>
        <Stack size="xxxs" justify="left">
          <FormSwitcher
            {...fields.productTab}
            labels={[translation.merchandise, translation.events]}
            label={translation.create.selectType}
            onChange={(val) => {
              clear();
              fields.terms.onChange([]);
              fields.categoryIds.onChange([]);
              fields.productTab.onChange(val);
            }}
          />
          <Stack size="xxs" justify="right" fullWidth>
            <ProductEventTable
              type={model.productTab ? 'products' : 'events'}
              items={mappedItems}
              selectedVariantIds={items}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAllDisplayed}
              disableSelectedItems
              emptyMessage={
                model.terms.length > 0
                  ? translation.table.noSearchResultsFound
                  : model.productTab
                    ? translation.table.noProductVariants
                    : translation.table.noEventsAvailable
              }
              actions={
                <ProductEventTableActions
                  searchTerms={model.terms}
                  categoryOptions={categories.map((e) => ({ id: e.id, label: e.name }))}
                  categories={model.categoryIds}
                  onCategoryChange={fields.categoryIds.onChange}
                  onSearchTermChange={fields.terms.onChange}
                  showCategoriesFilter={model.productTab}
                  disablePadding
                />
              }
            />
            {pageInfo?.hasNextPage && (
              <Button
                title={t.actions.loadMore}
                onClick={() => {
                  fetchMore({
                    variables: {
                      after: pageInfo.endCursor,
                    },
                  });
                }}
              />
            )}
          </Stack>
        </Stack>
      </FormForm>
    </Modal>
  );
}
